import { resolveCustomerFieldMappings } from '@/config/shared/commerce';
import type {
  SelectorAutoSource,
  SelectorAutoTrigger,
} from '@/config/shared/auto-source';
import type { SelectorType } from '@/config/shared/selector';
import { DEFAULT_SELECTOR_TYPE } from '@/config/shared/selector';
import type { ConversionEventData } from '@/events/conversion';
import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import { BaseAutoTracker } from '../../auto/BaseAutoTracker';
import { buildAutoSourceOrigin } from '../../auto/build-auto-source-origin';
import { CustomerExtractor, ProductExtractor } from '../../commerce';
import type {
  ConversionEventOrigin,
  ConversionEventType,
} from '../../contracts';
import { SelectorResolver } from '../SelectorResolver';
import type { TrackerFactory } from '../../tracks/TrackerFactory';
import { elementAllowsTrigger } from './elementTrigger';

export type ConfiguredSelectorOptions = {
  readonly eventType: ConversionEventType;
  readonly source: SelectorAutoSource;
  readonly origin?: ConversionEventOrigin;
};

type ResolvedTrigger = SelectorAutoTrigger | 'both';

/**
 * Detector de source `type: 'selector'`.
 * Extrai dados mínimos via extractors e dispara via TrackerFactory.trackAuto.
 */
export class ConfiguredSelectorDetector extends BaseAutoTracker {
  private readonly factory: TrackerFactory;

  private readonly config: ConfiguredSelectorOptions;

  private readonly resolver: SelectorResolver;

  private readonly selectorType: SelectorType;

  private readonly fireOnce: boolean;

  private readonly trigger: ResolvedTrigger;

  private readonly visibleThreshold: number;

  private readonly origin: ConversionEventOrigin;

  private readonly customerExtractor: CustomerExtractor;

  private readonly productExtractor: ProductExtractor;

  private readonly visibleFired = new WeakSet<Element>();

  private readonly observedElements = new Set<Element>();

  private intersectionObserver: IntersectionObserver | null = null;

  private hasFired = false;

  private destroyed = false;

  constructor(
    context: PixelRuntimeContext,
    factory: TrackerFactory,
    config: ConfiguredSelectorOptions
  ) {
    super(context);
    this.factory = factory;
    this.config = config;
    this.selectorType = config.source.selectorType ?? DEFAULT_SELECTOR_TYPE;
    this.fireOnce = config.source.fireOnce === true;
    this.trigger = config.source.trigger ?? 'both';
    this.visibleThreshold = normalizeVisibleThreshold(
      config.source.visibleThreshold
    );
    this.origin =
      config.origin ??
      buildAutoSourceOrigin('selector', config.eventType, config.source.name);
    this.resolver = new SelectorResolver(context.logger);

    const commerce = context.services.commerce.config;
    this.customerExtractor = new CustomerExtractor({
      fieldMappings: resolveCustomerFieldMappings(
        commerce?.customer,
        commerce?.mappings?.fields
      ),
    });
    this.productExtractor = new ProductExtractor({
      attributeMappings: commerce?.mappings?.attributes,
      fieldMappings: commerce?.mappings?.fields,
    });
  }

  get name(): string {
    return `ConfiguredSelector(${this.config.eventType}:${this.config.source.name})`;
  }

  protected onInit(): void {
    this.destroyed = false;
    this.hasFired = false;
    if (!this.isDocumentAvailable()) return;

    if (this.trigger === 'click' || this.trigger === 'both') {
      this.addDocumentListener('click', event => this.handleEvent(event), true);
    }
    if (this.trigger === 'submit' || this.trigger === 'both') {
      this.addDocumentListener(
        'submit',
        event => this.handleEvent(event),
        true
      );
    }
    if (this.trigger === 'hover') {
      this.addDocumentListener(
        'mouseover',
        event => this.handleHover(event),
        true
      );
    }
    if (this.trigger === 'visible') {
      this.startVisibleObserver();
    }
  }

  protected onDestroy(): void {
    this.destroyed = true;
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;
    this.observedElements.clear();
  }

  private handleHover(event: Event): void {
    if (!(event instanceof MouseEvent)) return;

    const matched = this.findMatchedElement(event);
    if (!matched) return;

    const related =
      event.relatedTarget instanceof Node ? event.relatedTarget : null;
    if (related && matched.contains(related)) return;

    this.dispatchFromElement(matched, 'hover');
  }

  private handleEvent(event: Event): void {
    if (this.trigger === 'click' && event.type !== 'click') return;
    if (this.trigger === 'submit' && event.type !== 'submit') return;

    const matched = this.findMatchedElement(event);
    if (!matched) return;

    this.dispatchFromElement(
      matched,
      event.type === 'submit' ? 'submit' : 'click'
    );
  }

  private startVisibleObserver(): void {
    if (typeof IntersectionObserver === 'undefined') {
      this.context.logger.debug(
        '[ConfiguredSelectorDetector] IntersectionObserver unavailable'
      );
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (
            this.visibleThreshold > 0 &&
            entry.intersectionRatio < this.visibleThreshold
          ) {
            continue;
          }
          this.handleVisible(entry.target);
        }
      },
      { threshold: this.visibleThreshold }
    );

    this.syncVisibleTargets();
    this.observeDom(() => this.syncVisibleTargets());
  }

  private syncVisibleTargets(): void {
    if (this.destroyed || !this.intersectionObserver) return;

    const matches = this.resolver
      .queryAll(this.config.source.selector, this.selectorType)
      .filter(element => elementAllowsTrigger(element, 'visible'));
    const next = new Set(matches);

    for (const element of this.observedElements) {
      if (next.has(element)) continue;
      this.intersectionObserver.unobserve(element);
      this.observedElements.delete(element);
    }

    for (const element of matches) {
      if (this.observedElements.has(element)) continue;
      this.intersectionObserver.observe(element);
      this.observedElements.add(element);
    }
  }

  private handleVisible(element: Element): void {
    if (this.visibleFired.has(element)) return;
    if (!this.dispatchFromElement(element, 'visible')) return;
    this.visibleFired.add(element);
  }

  private dispatchFromElement(
    element: Element,
    mode: SelectorAutoTrigger
  ): boolean {
    if (this.destroyed) return false;
    if (this.fireOnce && this.hasFired) return false;
    if (!elementAllowsTrigger(element, mode)) return false;

    try {
      const data = this.extractData(element);
      if (data === null) return false;

      this.factory.trackAuto(this.config.eventType, data, {
        origin: this.origin,
      });

      if (this.fireOnce) {
        this.hasFired = true;
      }
      return true;
    } catch (error) {
      this.context.logger.debug(
        `[ConfiguredSelectorDetector] ${this.config.eventType} ignored:`,
        error
      );
      return false;
    }
  }

  private findMatchedElement(event: Event): Element | null {
    const candidates: Element[] = [];

    if (event.type === 'submit' && event.target instanceof Element) {
      candidates.push(event.target);
    }

    let current: Element | null =
      event.target instanceof Element ? event.target : null;

    while (current && current !== document.documentElement) {
      candidates.push(current);
      current = current.parentElement;
    }

    for (const element of candidates) {
      if (
        this.resolver.matches(
          element,
          this.config.source.selector,
          this.selectorType
        )
      ) {
        return element;
      }
    }

    return null;
  }

  private extractData(element: Element): ConversionEventData | null {
    const { eventType } = this.config;

    if (eventType === 'LEAD') {
      const customer = this.customerExtractor.extractFromElement(element);
      if (!customer.email && !customer.phone && !customer.name) {
        this.context.logger.debug(
          '[ConfiguredSelectorDetector] LEAD skipped: no email/phone/name'
        );
        return null;
      }
      return {
        ...(customer.email ? { email: customer.email } : {}),
        ...(customer.phone ? { phone: customer.phone } : {}),
        ...(customer.name ? { name: customer.name } : {}),
      };
    }

    if (eventType === 'ADD_TO_CART' || eventType === 'REMOVE_FROM_CART') {
      const product = this.productExtractor.extractFromElement(element);
      if (eventType === 'REMOVE_FROM_CART') {
        return {
          ...(product.productId ? { productId: product.productId } : {}),
          ...(product.quantity != null ? { quantity: product.quantity } : {}),
        };
      }
      return {
        ...(product.productId ? { productId: product.productId } : {}),
        ...(product.productName ? { productName: product.productName } : {}),
        ...(product.price != null ? { price: product.price } : {}),
        ...(product.currency ? { currency: product.currency } : {}),
        ...(product.quantity != null ? { quantity: product.quantity } : {}),
      };
    }

    if (eventType === 'SEARCH') {
      const query =
        element.getAttribute('data-query') ??
        (typeof HTMLInputElement !== 'undefined' &&
        element instanceof HTMLInputElement
          ? element.value
          : '');
      if (!query.trim()) return null;
      return { query: query.trim() };
    }

    if (eventType === 'INITIATE_CHECKOUT' || eventType === 'PURCHASE') {
      return {};
    }

    if (eventType === 'VIEW_CONTENT') {
      const product = this.productExtractor.extractFromElement(element);
      return {
        ...(product.productId ? { contentId: product.productId } : {}),
        ...(product.price != null ? { value: product.price } : {}),
        ...(product.currency ? { currency: product.currency } : {}),
      };
    }

    return {};
  }
}

function normalizeVisibleThreshold(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}
